import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment as env } from '../../../environments/environment';
import { UsersService } from '../../@services/users.service';

/**
 * Snapshot of the cross-tab Cognito-cache reconcile state. Drives both the
 * SyncOverlayComponent (global banner) and the Users page's Refresh button
 * disabled state.
 *
 *   inProgress       — true between 'started' and 'finished' sync_state events
 *   byEmail          — operator who triggered the active sync (or null)
 *   startedAt        — ISO timestamp when the active sync began (or null)
 *   prevTotal        — denominator for the progress bar (cached on 'started')
 *   scannedSoFar     — numerator, polled from /sync-all/progress while inProgress
 *   error            — last finished-with-error message (cleared on next start)
 */
export interface SyncStateSnapshot {
  inProgress: boolean;
  byEmail: string | null;
  startedAt: string | null;
  prevTotal: number;
  scannedSoFar: number;
  error: string | null;
}

const POLL_INTERVAL_MS = 750;
const HEARTBEAT_RECONNECT_MS = 3000;

/**
 * Owns its own EventSource on /admin/users-live/stream so the sync overlay
 * appears on every admin page, not just the Users tab. Two SSE connections
 * (this + the users.component grid stream) is acceptable overhead — keeps
 * concerns separated and avoids refactoring the existing grid pipeline.
 *
 * On boot: GET /sync-all/progress to catch late-opening tabs (a sync may
 * already be in flight from another operator). On every 'started' event,
 * begins polling /sync-all/progress every 750ms for the determinate bar.
 * On 'finished', stops polling, hides overlay, emits onSyncFinished$ so
 * the Users component can refresh its grid with the post-sync payload.
 */
@Injectable({ providedIn: 'root' })
export class SyncStateService implements OnDestroy {
  public readonly state$ = new BehaviorSubject<SyncStateSnapshot>({
    inProgress: false,
    byEmail: null,
    startedAt: null,
    prevTotal: 0,
    scannedSoFar: 0,
    error: null,
  });

  /** Fires once when a sync transitions started → finished. Components subscribe to refresh their grids. */
  public readonly onSyncFinished$ = new BehaviorSubject<number>(0);

  private _eventSource: EventSource | null = null;
  private _reconnectTimer: any = null;
  private _progressPollTimer: any = null;
  private _bootProbeDone = false;

  constructor(
    private http: HttpClient,
    private usersService: UsersService,
    private zone: NgZone,
  ) {
    // Open the stream at app-boot. NgZone wrap so EventSource callbacks
    // trigger change detection on the overlay component.
    setTimeout(() => this._boot(), 0);
  }

  ngOnDestroy(): void {
    this._close_event_source();
    this._stop_polling();
  }

  private async _boot() {
    // Late-opening tab probe: if a sync is already in flight, get the snapshot
    // BEFORE the next event would arrive so the overlay shows immediately.
    await this._probe_progress();
    this._open_event_source();
  }

  /** Public — called by Users component after POSTing /sync-all to seed the overlay before SSE arrives. */
  public async refreshFromServer(): Promise<void> {
    await this._probe_progress();
  }

  private async _probe_progress() {
    try {
      const p: any = await this.http.get(
        `${env.apiUrl}/rest/admin/users/sync-all/progress`,
        { withCredentials: true },
      ).toPromise();
      this._bootProbeDone = true;
      const lock = p?.lock || {};
      if (lock.in_progress) {
        this._set_in_progress({
          byEmail: lock.by_email || null,
          startedAt: lock.started_at || null,
          prevTotal: Number(p?.prev_total ?? 0),
          scannedSoFar: Number(p?.scanned_so_far ?? 0),
        });
        this._start_polling();
      } else if (this.state$.value.inProgress) {
        // Probe disagrees with our cached state (likely missed the 'finished' event).
        this._set_finished({ error: null });
      }
    } catch {
      /* probe is best-effort; overlay will hydrate on next SSE event */
    }
  }

  private async _open_event_source() {
    if (this._eventSource) return;
    if (this._reconnectTimer) return;
    const token = await this.usersService.mintUsersStreamToken();
    if (!token) {
      this._schedule_reconnect();
      return;
    }
    try {
      const url = this.usersService.getUsersStreamUrl(token);
      const es = new EventSource(url);
      this._eventSource = es;
      es.onmessage = (ev: MessageEvent) => {
        if (!ev?.data) return;
        try {
          const evt = JSON.parse(ev.data);
          if (evt?.op !== 'sync_state') return; // ignore per-row events
          this.zone.run(() => this._handle_sync_event(evt));
        } catch { /* malformed */ }
      };
      es.onerror = () => {
        // Token expires every 60s; on disconnect close + re-mint.
        this._close_event_source();
        this._schedule_reconnect();
      };
    } catch {
      this._eventSource = null;
      this._schedule_reconnect();
    }
  }

  private _schedule_reconnect() {
    if (this._reconnectTimer) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._open_event_source();
    }, HEARTBEAT_RECONNECT_MS);
  }

  private _close_event_source() {
    if (this._eventSource) {
      try { this._eventSource.close(); } catch { /* ignore */ }
      this._eventSource = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  private _handle_sync_event(evt: any) {
    if (evt.state === 'started') {
      this._set_in_progress({
        byEmail: evt.by_email || null,
        startedAt: evt.started_at || null,
        prevTotal: Number(evt.prev_total ?? 0),
        scannedSoFar: 0,
      });
      this._start_polling();
    } else if (evt.state === 'finished') {
      this._set_finished({ error: evt.error || null });
    }
  }

  private _set_in_progress(p: { byEmail: string | null; startedAt: string | null; prevTotal: number; scannedSoFar: number }) {
    this.state$.next({
      inProgress: true,
      byEmail: p.byEmail,
      startedAt: p.startedAt,
      prevTotal: p.prevTotal,
      scannedSoFar: p.scannedSoFar,
      error: null,
    });
  }

  private _set_finished(p: { error: string | null }) {
    this._stop_polling();
    const prior = this.state$.value;
    this.state$.next({
      inProgress: false,
      byEmail: null,
      startedAt: null,
      prevTotal: prior.prevTotal,
      scannedSoFar: 0,
      error: p.error,
    });
    // Notify subscribers (e.g. Users component) to refresh their data.
    this.onSyncFinished$.next(Date.now());
  }

  private _start_polling() {
    if (this._progressPollTimer) return;
    this._progressPollTimer = setInterval(() => this._poll_progress(), POLL_INTERVAL_MS);
    // Fire once immediately so the bar isn't stuck at 0 for the first tick.
    this._poll_progress();
  }

  private _stop_polling() {
    if (this._progressPollTimer) {
      clearInterval(this._progressPollTimer);
      this._progressPollTimer = null;
    }
  }

  private async _poll_progress() {
    try {
      const p: any = await this.http.get(
        `${env.apiUrl}/rest/admin/users/sync-all/progress`,
        { withCredentials: true },
      ).toPromise();
      const cur = this.state$.value;
      if (!cur.inProgress) return; // sync finished between scheduling + return
      // Self-recovery: if the lock is gone (no sync actually running on the
      // server) but our local state still thinks one is in flight, transition
      // to finished. Catches the case where a 'finished' SSE event was
      // missed (server restart mid-sync, browser tab suspended, network
      // glitch). Without this guard the overlay sticks forever + the
      // Refresh button stays disabled.
      const lockInProgress = p?.lock?.in_progress === true;
      if (!lockInProgress) {
        this._set_finished({ error: null });
        return;
      }
      this.state$.next({
        ...cur,
        scannedSoFar: Number(p?.scanned_so_far ?? cur.scannedSoFar),
        prevTotal: Number(p?.prev_total ?? cur.prevTotal),
      });
    } catch {
      /* polling is best-effort UX */
    }
  }
}
